import { CdkMenuModule } from '@angular/cdk/menu';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  ViewEncapsulation,
  booleanAttribute,
  effect,
  inject,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { FormValueControl } from '@angular/forms/signals';
import { YouTubePlayer } from '@angular/youtube-player';
import { Editor } from '@tiptap/core';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { TableKit } from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyleKit } from '@tiptap/extension-text-style';

import StarterKit from '@tiptap/starter-kit';
import { Node } from 'prosemirror-model';
import { Transaction } from 'prosemirror-state';
import { fontSizes } from './consts/font-sizes.const';
import { StorageService } from '@api-client';
import { ResizableImage } from './resizable-image';
import { CustomYoutube } from './custom-youtube';

@Component({
  selector: 'lepi-editor-v2',
  standalone: true,
  imports: [CommonModule, CdkMenuModule, YouTubePlayer],
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class EditorV2Component
  implements AfterViewInit, FormValueControl<string>
{
  baseUrl = input<string>('');

  value = model<string>('');
  touched = model<boolean>(false);

  editorRef = viewChild<ElementRef<HTMLElement>>('editorRef');
  format = input<'html' | 'json'>('html');
  image = input<boolean, string | boolean>(true, {
    transform: booleanAttribute,
  });
  viewer = input<boolean, string | boolean>(false, {
    transform: booleanAttribute,
  });

  fontSizes = fontSizes;
  fontSize = signal<string>('16px');

  public editor = signal<Editor | null>(null);

  isCurrentNodeTable = signal<boolean>(false);

  private readonly storageService = inject(StorageService);
  private isUpdatingFromModel = false;

  constructor() {
    // value model의 변화를 감지하여 에디터 내용 업데이트
    effect(() => {
      const currentValue = this.value();
      const ed = this.editor();
      if (ed && !this.isUpdatingFromModel && currentValue) {
        // 현재 에디터 내용과 비교하여 다를 때만 업데이트
        const editorContent = ed.getHTML();
        if (currentValue !== editorContent) {
          this.updateEditorContent(currentValue);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.initializeEditor();
  }

  handleCommand(command: any, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    command();
  }

  private initializeEditor() {
    if (this.editor()) return;
    this.editor.set(
      new Editor({
        element: this.editorRef()?.nativeElement,
        extensions: [
          StarterKit.configure({
            paragraph: {
              HTMLAttributes: {
                class: 'min-h-6',
              },
            },
            bulletList: {
              HTMLAttributes: {
                class: 'list-disc ml-5',
              },
            },
            orderedList: {
              HTMLAttributes: {
                class: 'list-decimal ml-5',
              },
            },
            link: {
              autolink: true,
              linkOnPaste: true,
              defaultProtocol: 'https',
              openOnClick: true,
              HTMLAttributes: {
                class: 'text-blue-500 underline',
              },
            },
          }),
          ResizableImage,
          TextStyleKit,
          TextAlign.configure({
            types: ['paragraph', 'heading', 'image', 'youtube'],
          }),
          Highlight.configure({
            multicolor: true,
          }),
          Superscript,
          Subscript,
          TableKit.configure({
            table: {
              resizable: true,
            },
          }),
          CustomYoutube.configure({
            inline: false,
          }),
        ],
        onTransaction: ({ transaction }) => {
          // check if image node was deleted
          this.handleImageDelete(transaction);
        },
        onUpdate: ({ editor }) => {
          this.isUpdatingFromModel = true;
          this.touched.set(true);
          switch (this.format()) {
            case 'html':
              this.value.set(editor.getHTML());
              break;
            case 'json':
              this.value.set(JSON.stringify(editor.getJSON()));
              break;
          }
          this.isUpdatingFromModel = false;
        },
        onCreate: ({ editor }) => {
          editor.view.dom.spellcheck = false;
          editor.commands.setFontSize('16px');
          // 초기 값이 있으면 적용
          const initialValue = this.value();
          if (initialValue) {
            this.updateEditorContent(initialValue);
          }
        },
      })
    );
  }

  insertLinkWithText(url: string, linkText?: string) {
    const ed = this.editor();
    if (!ed || !url) return;

    const { from, to } = ed.state.selection;
    const selectedText = ed.state.doc.textBetween(from, to);

    // 표시할 텍스트 결정 (우선순위: 선택된 텍스트 > 입력된 링크 텍스트 > URL)
    const displayText = selectedText || linkText || url;

    if (selectedText) {
      // 선택된 텍스트가 있는 경우: 해당 텍스트에 링크 적용
      ed.chain().focus().setLink({ href: url, target: '_blank' }).run();
    } else {
      // 선택된 텍스트가 없는 경우: 새 링크 삽입
      ed.chain()
        .focus()
        .insertContent(`<a href="${url}" target="_blank">${displayText}</a> `)
        .run();
    }
  }

  clearLinkInputs(...inputs: HTMLInputElement[]) {
    inputs.forEach(input => (input.value = ''));
  }

  selectImage(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    const a = document.createElement('input');
    a.type = 'file';
    a.accept = 'image/*';
    a.click();
    a.onchange = async e => {
      const file = (e.target as any)?.files[0];

      if (!file) {
        return;
      }

      try {
        // 파일을 서버에 업로드
        const uploadedFiles = await new Promise<any[]>((resolve, reject) => {
          this.storageService
            .storageControllerUploadFilesV1({
              body: {
                files: [file],
              },
            })
            .subscribe({
              next: res => resolve(res),
              error: err => reject(err),
            });
        });

        if (uploadedFiles && uploadedFiles.length > 0) {
          // 업로드된 파일의 URL 사용
          const imageUrl = uploadedFiles[0].url;
          this.editor()
            ?.chain()
            .focus()
            .setImage({
              src: imageUrl,
            })
            .run();
        }
      } catch (error) {
        console.error('이미지 업로드 실패:', error);
      }
    };
  }

  handleImageDelete(transaction: Transaction) {
    const current: Node[] = [];
    transaction.doc.content.forEach(node => {
      if (node.type.name == 'image') {
        current.push(node);
      }
    });
    const before: Node[] = [];
    transaction.before.content.forEach(node => {
      if (node.type.name == 'image') {
        before.push(node);
      }
    });
    if (!current || before.length == 0) {
      return;
    }

    const deletedImageNodes = before.filter(node => {
      const src = node.attrs['src'];
      return !current.find(curNode => curNode.attrs['src'] == src);
    });

    if (deletedImageNodes.length > 0) {
      deletedImageNodes.forEach(async _node => {
        // await this.uploadService.delete(node.attrs['src']);
      });
    }
  }

  addYoutube(src: string) {
    const isShorts = src.includes('youtube.com/shorts/');
    this.editor()
      ?.chain()
      .focus()
      .insertContent({
        type: 'youtube',
        attrs: { src, isShorts },
      })
      .run();
  }

  isYoutubeUrl(url: string) {
    const isHostYoutube = (url: string) =>
      ['youtube.com', 'youtu.be'].some(host => url?.includes(host));

    const hasVideoId = (url: string) => {
      const videoId = this.getYouTubeVideoId(url);
      return !!videoId;
    };

    return isHostYoutube(url) && hasVideoId(url);
  }

  getYouTubeVideoId(url: string): string | undefined {
    try {
      const parsedUrl = new URL(url);
      const hostname = parsedUrl.hostname;
      const pathname = parsedUrl.pathname;
      const searchParams = parsedUrl.searchParams;

      if (hostname === 'youtu.be') {
        return pathname.slice(1);
      }

      if (searchParams.has('v')) {
        return searchParams.get('v') || undefined;
      }
      if (pathname.startsWith('/live/')) {
        return pathname.split('/live/')[1]?.split('?')[0] || undefined;
      }
      if (pathname.startsWith('/shorts/')) {
        return pathname.split('/shorts/')[1]?.split('?')[0] || undefined;
      }

      return undefined;
    } catch {
      return undefined;
    }
  }

  private updateEditorContent(content: string): void {
    const ed = this.editor();
    if (!ed || !content) return;

    if (this.format() === 'html') {
      ed.commands.setContent(content);
    } else {
      try {
        ed.commands.setContent(JSON.parse(content));
      } catch {
        console.error('Invalid JSON content');
      }
    }
  }
}
